<template>
	<v-card class="elevation-0 events-page page">
		<PageBlocked v-if="isBlocked" :blockRole="blockRole" />
		<div v-else-if="isBlocked !== null">
			<div class="events-page__wrap">
				<FilterChipButtons
					:isButtonActive="isButtonActive"
					:chipsList="chipsList"
					:clearFilter="clearFilter"
					@handleChipClick="handleChipClick"
				/>

				<div v-if="filterModalIsVisible">
					<FilterModal
						:filterModalIsVisible="filterModalIsVisible"
						:currentModalFilter="currentModalFilter"
						:currentModalLabel="currentModalLabel"
						:selectedChips="selectedChips"
						:filters="filters"
						:dateError="date.error"
						:dateOption="date.option"
						:dateRange="date.selectedRange"
						@closeModal="closeModal"
						@applySelection="applySelection"
						@setItem="setItem"
					/>
				</div>
			</div>

			<Banner v-if="banner && !isLoading" :banner="banner" :mt="20" />

			<div class="d-flex mt-3">
				<v-skeleton-loader
					v-if="isLoading"
					v-bind="attrs"
					width="600"
					type="list-item, list-item-three-line, card-heading"
				/>
				<div v-else style="width: 100%" class="events_box">
					<div v-if="events.length === 0" class="mt-5">
						<NewsFeedEmpty
							title="Events is empty at the moment."
							description="Check back later."
						/>
					</div>

					<div v-if="events.length" class="events-page__wrapper">
						<div v-for="{ month, data } in groupedByMonth" :key="month">
							<h3 class="events-page__time mb-2">
								{{ month }}
							</h3>
							<div v-for="(event, index) in data" :key="index">
								<EventPreview :event="event" />
							</div>
						</div>
					</div>
				</div>
			</div>

			<div
				class="d-flex my-9 justify-center home-page__pagination"
				v-if="!isLoading && events.length > 0"
			>
				<Pagination
					:page="filters.page"
					:pageCount="+this.pageCountEvents"
					v-on:pageChange="onPageChange"
				/>
			</div>

			<v-btn v-show="isVisible" @click="scrollToTop" bottom right fixed fab>
				<v-icon>mdi-chevron-up</v-icon>
			</v-btn>
		</div>
	</v-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

import EventPreview from '@/components/Event/EventPreview'
import NewsFeedEmpty from '@/components/Post/NewsFeedEmpty'
import Pagination from '@/components/Pagination.vue'
import Block from '@/mixins/Block.js'
import PageBlocked from '@/components/PageBlocked'
import FilterChipButtons from '@/components/FilterChipButtons.vue'
import Filter from '@/mixins/Filter'
import FilterModal from '@/components/Modals/FilterModal'
import Banner from '@/components/Banner'

export default {
	name: 'EventsPage',
	mixins: [Block, Filter],
	components: {
		FilterChipButtons,
		EventPreview,
		NewsFeedEmpty,
		Pagination,
		PageBlocked,
		FilterModal,
		Banner,
	},
	methods: {
		...mapActions('event', ['fetchEvents', 'clearEventCategories']),
		...mapActions('advertisement', ['getBanner', 'clearBanner']),

		toggleVisibility() {
			this.isVisible = window.scrollY > 1000
		},
		scrollToTop() {
			window.scrollTo({
				top: 0,
				behavior: 'smooth',
			})
		},
	},

	created() {
		if (!this.isBlocked || this.isBlocked === null) {
			this.fetchEvents(this.requestQuery)
			const bannerQuery = {
				places: ['event_list'],
				page: this.filters.page,
			}
			if (this.selectedChips.chapters && this.selectedChips.chapters.length) {
				bannerQuery.chapter_id = this.selectedChips.chapters
			}
			this.getBanner(bannerQuery)
		}
	},

	computed: {
		...mapGetters('event', ['events', 'isLoading', 'pageCountEvents']),
		...mapGetters('settings', ['eventSettings']),
		...mapGetters('advertisement', ['banner']),
		groupedByMonth() {
			const result = []
			this.events.forEach((event) => {
				const fromDate = new Date(event.event_start * 1000)
				const month = fromDate.toLocaleString('en-US', { month: 'long' })
				const day = fromDate.getDate()
				const year = fromDate.getFullYear()
				const key = `${month} ${day}, ${year}`

				if (result.length === 0) {
					result.push({
						date: key,
						data: [event],
					})
				} else {
					const lastGroup = result[result.length - 1]
					if (lastGroup.date === key) {
						lastGroup.data.push(event)
					} else {
						result.push({
							date: key,
							data: [event],
						})

					}
				}
			})
			return result.map(({ date, data }) => ({ date, data }))
		},
	},

	mounted() {
		window.addEventListener('scroll', this.toggleVisibility)
	},
	beforeDestroy() {
		window.removeEventListener('scroll', this.toggleVisibility)
		this.clearEventCategories()
		this.clearBanner()
	},

	data() {
		return {
			attrs: {
				class: 'mb-6 mt-6',
				elevation: 0,
			},
			isVisible: false,
			pageBlockedName: 'events',
		}
	},
}
</script>

<style lang="scss">
.events-page {
	background: #f1f1f1 !important;
	max-width: 600px;
	width: 100%;

	&__divider {
		border-color: #000 !important;
		mix-blend-mode: normal;
		opacity: 0.1;
	}

	&__time {
		color: rgb(74, 74, 74);
		font-size: 24px;
		font-family: Montserrat, sans-serif;
		font-weight: bold;
	}

	.event {
		&__search {
			background: white;
			height: 54px;
			border-radius: 10px;
			border: 1px solid rgb(230, 230, 230);
			max-width: 600px;
		}

		&__filters {
			color: white;
			font-size: 14px;
			font-family: Montserrat, sans-serif;
			font-weight: bold;
			letter-spacing: 0.36px;
			margin-top: 20px;
			background: rgb(65, 161, 216) !important;
			border-radius: 15px;
			box-shadow: 0 1px 1px 0 rgba(0, 0, 0, 0.1);
			text-transform: capitalize !important;
			align-items: center;
		}

		@media screen and (max-width: 1264px) {
			&__search {
				max-width: none;
			}
		}
	}

	.post {
		&__search {
			background: white;
			height: 54px;
			border-radius: 10px;
			border: 1px solid rgb(230, 230, 230);
			max-width: 600px;
		}

		@media screen and (max-width: 1264px) {
			&__search {
				max-width: none;
				width: 100%;
			}
		}
	}
}
</style>
